import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  AreaChart, Area, PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, Target, Building2, Users, Zap, Activity } from 'lucide-react';
import TamBreakdownCard from './TamBreakdownCard';
import { APP_CHART, healthChartColour } from '../../lib/chartTheme';
import type { TerritoryAnalyticsSnapshot, Account } from '../../types';

interface Props {
  snapshot: TerritoryAnalyticsSnapshot | null;
  history: TerritoryAnalyticsSnapshot[];
  accounts: Account[];
}

const TIER_COLORS: Record<string, string> = { A: APP_CHART.brand, B: APP_CHART.focus, C: APP_CHART.accent, untiered: APP_CHART.muted };

function TrendBadge({ current, previous }: { current: number; previous: number }) {
  const diff = current - previous;
  if (Math.abs(diff) < 0.5) return <Minus className="w-3.5 h-3.5 text-app-muted" />;
  if (diff > 0) return <TrendingUp className="w-3.5 h-3.5 text-forest-500" />;
  return <TrendingDown className="w-3.5 h-3.5 text-app-critical" />;
}

function StatCard({ label, value, icon: Icon, trend, subtitle }: {
  label: string; value: string | number; icon: typeof Target; trend?: { current: number; previous: number }; subtitle?: string;
}) {
  return (
    <div className="bg-app-surface rounded-lg border border-app-line p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 bg-app-canvas rounded-lg flex items-center justify-center">
          <Icon className="w-4 h-4 text-app-muted" />
        </div>
        {trend && <TrendBadge current={trend.current} previous={trend.previous} />}
      </div>
      <p className="text-2xl font-bold text-app-ink">{value}</p>
      <p className="text-xs text-app-muted mt-0.5">{label}</p>
      {subtitle && <p className="text-[10px] text-app-muted mt-0.5">{subtitle}</p>}
    </div>
  );
}

function HealthGauge({ score }: { score: number }) {
  const clampedScore = Math.max(0, Math.min(100, Math.round(score)));
  const color = healthChartColour(clampedScore);
  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (clampedScore / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-36">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r="54" fill="none" stroke={APP_CHART.line} strokeWidth="8" />
          <circle
            cx="60" cy="60" r="54" fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" className="transition duration-700"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold" style={{ color }}>{clampedScore}</span>
          <span className="text-[10px] text-app-muted uppercase tracking-wider">Health</span>
        </div>
      </div>
    </div>
  );
}

function buildLiveSnapshot(accounts: Account[]): TerritoryAnalyticsSnapshot | null {
  if (accounts.length === 0) return null;
  const tierA = accounts.filter(a => a.tier === 'A').length;
  const tierB = accounts.filter(a => a.tier === 'B').length;
  const tierC = accounts.filter(a => a.tier === 'C').length;
  const untiered = accounts.filter(a => !a.tier).length;
  const tieredAccounts = accounts.filter(a => a.tier_score > 0);
  const avgScore = tieredAccounts.length > 0
    ? tieredAccounts.reduce((s, a) => s + (a.tier_score || 0), 0) / tieredAccounts.length
    : 0;
  const enriched = accounts.filter(a => a.enrichment_status === 'completed').length;
  const enrichPct = accounts.length > 0 ? (enriched / accounts.length) * 100 : 0;
  const industries: Record<string, number> = {};
  for (const a of accounts) {
    if (a.industry) industries[a.industry] = (industries[a.industry] || 0) + 1;
  }
  const tierBalance = untiered === 0 && accounts.length > 0 ? 80 : Math.max(0, 80 - untiered * 10);
  const ucCoverage = 60;
  const healthScore = Math.round((tierBalance + enrichPct + avgScore) / 3);

  return {
    id: 'live',
    org_id: '',
    user_id: '',
    snapshot_date: new Date().toISOString().split('T')[0],
    role_at_snapshot: 'member',
    accounts_owned: accounts.length,
    accounts_team_member: 0,
    total_tam_min: accounts.reduce((s, a) => s + (a.overview_tam_min || 0), 0),
    total_tam_max: accounts.reduce((s, a) => s + (a.overview_tam_max || 0), 0),
    accounts_tiered_a: tierA,
    accounts_tiered_b: tierB,
    accounts_tiered_c: tierC,
    accounts_untiered: untiered,
    avg_tier_score: avgScore,
    max_tier_score: tieredAccounts.length > 0 ? Math.max(...tieredAccounts.map(a => a.tier_score || 0)) : 0,
    accounts_with_use_cases: 0,
    avg_use_case_score: 0,
    total_contacts: 0,
    total_actions_7d: 0,
    total_actions_30d: 0,
    actions_by_type: {},
    enrichment_complete_pct: enrichPct,
    industry_breakdown: industries,
    icp_alignment_score: avgScore,
    territory_health_score: healthScore,
    health_components: {
      tier_balance: tierBalance,
      enrichment: enrichPct,
      use_case_coverage: ucCoverage,
      use_case_quality: 0,
      activity: 0,
      icp_alignment: avgScore,
    },
    suggested_accounts: [],
    created_at: new Date().toISOString(),
  } as TerritoryAnalyticsSnapshot;
}

export default function MemberDashboard({ snapshot: snapshotProp, history, accounts }: Props) {
  const navigate = useNavigate();
  const snapshot = snapshotProp || buildLiveSnapshot(accounts);
  const prev = history.length > 1 ? history[1] : null;

  const tierData = useMemo(() => {
    if (!snapshot) return [];
    return [
      { name: 'Tier A', value: snapshot.accounts_tiered_a, fill: TIER_COLORS.A },
      { name: 'Tier B', value: snapshot.accounts_tiered_b, fill: TIER_COLORS.B },
      { name: 'Tier C', value: snapshot.accounts_tiered_c, fill: TIER_COLORS.C },
      { name: 'Untiered', value: snapshot.accounts_untiered, fill: TIER_COLORS.untiered },
    ];
  }, [snapshot]);

  const healthRadar = useMemo(() => {
    if (!snapshot?.health_components) return [];
    const c = snapshot.health_components;
    return [
      { subject: 'Tier Balance', value: c.tier_balance },
      { subject: 'Enrichment', value: c.enrichment },
      { subject: 'UC Coverage', value: c.use_case_coverage },
      { subject: 'UC Quality', value: c.use_case_quality },
      { subject: 'Activity', value: c.activity },
      { subject: 'ICP Fit', value: c.icp_alignment },
    ];
  }, [snapshot]);

  const healthHistory = useMemo(() => {
    return [...history].reverse().map(h => ({
      date: new Date(h.snapshot_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
      health: Math.round(h.territory_health_score),
    }));
  }, [history]);

  const industryData = useMemo(() => {
    if (!snapshot?.industry_breakdown) return [];
    return Object.entries(snapshot.industry_breakdown)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, count]) => ({ name: name.length > 15 ? name.slice(0, 15) + '...' : name, count }));
  }, [snapshot]);

  const stackRankedAccounts = useMemo(() => {
    return [...accounts]
      .sort((a, b) => (b.tier_score || 0) - (a.tier_score || 0))
      .slice(0, 15);
  }, [accounts]);

  if (!snapshot) {
    return (
      <div className="text-center py-20">
        <Activity className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
        <p className="text-sm text-app-muted">No accounts in your territory yet.</p>
        <p className="text-xs text-app-muted mt-1">Add accounts to see your territory analytics.</p>
      </div>
    );
  }

  const totalAccounts = snapshot.accounts_owned + snapshot.accounts_team_member;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Accounts"
          value={totalAccounts}
          icon={Building2}
          trend={prev ? { current: totalAccounts, previous: prev.accounts_owned + prev.accounts_team_member } : undefined}
          subtitle={`${snapshot.accounts_owned} owned, ${snapshot.accounts_team_member} team`}
        />
        <StatCard
          label="Avg Tier Score"
          value={snapshot.avg_tier_score?.toFixed(1) || '0'}
          icon={Target}
          trend={prev ? { current: snapshot.avg_tier_score, previous: prev.avg_tier_score } : undefined}
        />
        <StatCard
          label="Total Contacts"
          value={snapshot.total_contacts}
          icon={Users}
          trend={prev ? { current: snapshot.total_contacts, previous: prev.total_contacts } : undefined}
        />
        <StatCard
          label="Actions (30d)"
          value={snapshot.total_actions_30d}
          icon={Zap}
          trend={prev ? { current: snapshot.total_actions_30d, previous: prev.total_actions_30d } : undefined}
        />
      </div>

      <TamBreakdownCard accounts={accounts} title="My TAM Breakdown" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-app-surface rounded-lg border border-app-line p-6 flex flex-col items-center justify-center">
          <h3 className="text-sm font-semibold text-app-ink mb-4">Territory Health</h3>
          <HealthGauge score={snapshot.territory_health_score} />
          <div className="mt-3 text-center">
            <p className="text-xs text-app-muted">Enrichment: {snapshot.enrichment_complete_pct?.toFixed(0)}%</p>
            <p className="text-xs text-app-muted">ICP Alignment: {snapshot.icp_alignment_score?.toFixed(0)}%</p>
          </div>
        </div>

        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <h3 className="text-sm font-semibold text-app-ink mb-4">Health Components</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={healthRadar}>
              <PolarGrid stroke={APP_CHART.line} />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: APP_CHART.muted }} />
              <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
              <Radar dataKey="value" stroke={APP_CHART.brand} fill={APP_CHART.brand} fillOpacity={0.15} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <h3 className="text-sm font-semibold text-app-ink mb-4">Health Trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={healthHistory}>
              <defs>
                <linearGradient id="healthGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={APP_CHART.brand} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={APP_CHART.brand} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={APP_CHART.line} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: APP_CHART.muted }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: APP_CHART.muted }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Area type="monotone" dataKey="health" stroke={APP_CHART.brand} fill="url(#healthGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <h3 className="text-sm font-semibold text-app-ink mb-4">Tier Distribution</h3>
          <div className="flex items-center gap-8">
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie data={tierData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}>
                  {tierData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {tierData.map(t => (
                <div key={t.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: t.fill }} />
                  <span className="text-xs text-app-muted">{t.name}: {t.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <h3 className="text-sm font-semibold text-app-ink mb-4">Industry Breakdown</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={industryData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={APP_CHART.line} />
              <XAxis type="number" tick={{ fontSize: 10, fill: APP_CHART.muted }} />
              <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10, fill: APP_CHART.muted }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="count" fill={APP_CHART.brand} radius={[0, 4, 4, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-app-surface rounded-lg border border-app-line overflow-hidden">
        <div className="px-6 py-4 border-b border-app-line">
          <h3 className="text-sm font-semibold text-app-ink">Account Stack Rank</h3>
          <p className="text-xs text-app-muted mt-0.5">Top accounts by composite score</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-app-canvas/60 text-[10px] font-semibold uppercase tracking-wider text-app-muted">
                <th className="px-4 py-2 text-left w-10">#</th>
                <th className="px-4 py-2 text-left">Account</th>
                <th className="px-4 py-2 text-left">Industry</th>
                <th className="px-4 py-2 text-center">Tier</th>
                <th className="px-4 py-2 text-right">Score</th>
                <th className="px-4 py-2 text-right">Progress</th>
                <th className="px-4 py-2 text-center">Enriched</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-app-line">
              {stackRankedAccounts.map((acct, i) => (
                <tr key={acct.id} onClick={() => navigate(`/accounts/${acct.id}`)} className="hover:bg-forest-50/50 transition-colors cursor-pointer">
                  <td className="px-4 py-2.5 text-xs text-app-muted font-mono">{i + 1}</td>
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-app-ink truncate max-w-[200px]">{acct.company_name}</p>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-app-muted truncate max-w-[120px]">{acct.industry || '-'}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-full ${
                      acct.tier === 'A' ? 'bg-forest-50 text-forest-700' :
                      acct.tier === 'B' ? 'bg-app-brand/10 text-app-brand' :
                      acct.tier === 'C' ? 'bg-app-warning/10 text-app-warning' :
                      'bg-app-canvas text-app-muted'
                    }`}>
                      {acct.tier || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs font-mono text-app-ink">{acct.tier_score?.toFixed(1) || '-'}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label={`${acct.company_name} tier score`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(100, acct.tier_score || 0)}>
                        <div
                          className="h-full rounded-full bg-app-accent transition"
                          style={{ width: `${Math.min(100, (acct.tier_score || 0))}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${
                      acct.enrichment_status === 'completed' ? 'bg-forest-400' :
                      acct.enrichment_status === 'processing' ? 'bg-app-warning animate-pulse' :
                      'bg-app-line-strong'
                    }`} />
                  </td>
                </tr>
              ))}
              {stackRankedAccounts.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-xs text-app-muted">No accounts found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
